import React, { useEffect, useState, useCallback } from 'react';
import { useParams, Link } from 'react-router-dom';
import { getReviewCards, reviewCard, Card } from '../api';

export default function ReviewPage() {
  const { id } = useParams<{ id: string }>();
  const deckId = Number(id);

  const [cards, setCards] = useState<Card[]>([]);
  const [index, setIndex] = useState(0);
  const [flipped, setFlipped] = useState(false);
  const [loading, setLoading] = useState(true);
  const [done, setDone] = useState(false);
  const [reviewed, setReviewed] = useState(0);
  const [stats, setStats] = useState({ again: 0, hard: 0, good: 0, easy: 0 });

  useEffect(() => {
    getReviewCards(deckId).then(data => {
      setCards(data);
      setLoading(false);
    });
  }, [deckId]);

  const currentCard = cards[index];
  const isJa = currentCard?.front?.match(/[\u3040-\u30ff\u4e00-\u9faf]/);

  const handleQuality = useCallback(async (quality: number, label: keyof typeof stats) => {
    if (!currentCard) return;
    await reviewCard(currentCard.id, quality);
    setReviewed(r => r + 1);
    setStats(s => ({ ...s, [label]: s[label] + 1 }));
    setFlipped(false);
    setTimeout(() => {
      if (index + 1 >= cards.length) setDone(true);
      else setIndex(i => i + 1);
    }, 80);
  }, [currentCard, index, cards.length]);

  useEffect(() => {
    const handler = (e: KeyboardEvent) => {
      if (e.key === ' ' || e.key === 'Enter') { e.preventDefault(); setFlipped(f => !f); }
      if (flipped) {
        if (e.key === '1') handleQuality(1, 'again');
        if (e.key === '2') handleQuality(3, 'hard');
        if (e.key === '3') handleQuality(4, 'good');
        if (e.key === '4') handleQuality(5, 'easy');
      }
    };
    window.addEventListener('keydown', handler);
    return () => window.removeEventListener('keydown', handler);
  }, [flipped, handleQuality]);

  if (loading) return <div className="loading"><div className="spinner" />Đang tải danh sách ôn...</div>;

  if (cards.length === 0) return (
    <div className="flashcard-page">
      <Link to={`/deck/${deckId}`} className="back-link">← Quay lại</Link>
      <div className="quiz-result">
        <div className="result-emoji">✅</div>
        <div className="result-score" style={{ fontSize: '1.5rem', color: 'var(--jade)' }}>Không có thẻ cần ôn!</div>
        <div className="result-label" style={{ marginBottom: '2rem' }}>Bạn đã hoàn thành tất cả việc ôn tập hôm nay. Hẹn gặp lại ngày mai! 🌟</div>
        <div className="result-actions">
          <Link to={`/deck/${deckId}/flashcard`} className="btn btn-jade btn-lg">🃏 Học Flashcard</Link>
          <Link to={`/deck/${deckId}`} className="btn btn-ghost btn-lg">← Về bộ từ</Link>
        </div>
      </div>
    </div>
  );

  if (done) {
    const total = stats.again + stats.hard + stats.good + stats.easy;
    const remembered = stats.good + stats.easy;
    return (
      <div className="flashcard-page">
        <Link to={`/deck/${deckId}`} className="back-link">← Quay lại</Link>
        <div className="quiz-result">
          <div className="result-emoji">🔁</div>
          <div className="result-score">{reviewed}</div>
          <div className="result-label">thẻ đã ôn tập</div>
          {/* Stats breakdown */}
          <div style={{ display: 'grid', gridTemplateColumns: 'repeat(4,1fr)', gap: '0.75rem', margin: '1.5rem 0' }}>
            {[
              { label: '😵 Quên', val: stats.again, color: '#dc2626', bg: '#fee2e2' },
              { label: '😓 Khó', val: stats.hard, color: '#d97706', bg: '#fef3c7' },
              { label: '😊 Nhớ', val: stats.good, color: '#059669', bg: '#d1fae5' },
              { label: '🚀 Dễ', val: stats.easy, color: '#2563eb', bg: '#dbeafe' },
            ].map(item => (
              <div key={item.label} style={{ background: item.bg, borderRadius: '10px', padding: '0.75rem', textAlign: 'center' }}>
                <div style={{ fontSize: '1.5rem', fontWeight: 700, color: item.color }}>{item.val}</div>
                <div style={{ fontSize: '0.72rem', color: item.color }}>{item.label}</div>
              </div>
            ))}
          </div>
          <div style={{ color: 'var(--ink-muted)', fontSize: '0.875rem', marginBottom: '2rem' }}>
            Ghi nhớ tốt: {remembered}/{total} ({total > 0 ? Math.round((remembered / total) * 100) : 0}%)
          </div>
          <div className="result-actions">
            <Link to={`/deck/${deckId}`} className="btn btn-ghost btn-lg">← Về bộ từ</Link>
          </div>
        </div>
      </div>
    );
  }

  const progress = (index / cards.length) * 100;

  // Calculate next interval preview
  const getNextInterval = (q: number) => {
    if (q < 3) return '1 ngày';
    if (currentCard.repetitions === 0) return '1 ngày';
    if (currentCard.repetitions === 1) return '6 ngày';
    return `~${Math.round(currentCard.interval * currentCard.ease_factor)} ngày`;
  };

  return (
    <div className="flashcard-page">
      <Link to={`/deck/${deckId}`} className="back-link">← Quay lại</Link>

      <div style={{ display: 'flex', alignItems: 'center', gap: '0.75rem', marginBottom: '0.5rem' }}>
        <span style={{ fontSize: '0.8rem', background: 'var(--gold-light)', color: 'var(--gold)', padding: '3px 10px', borderRadius: '20px', fontWeight: 600 }}>
          🔁 Ôn tập SRS
        </span>
        <span style={{ fontSize: '0.8rem', color: 'var(--ink-muted)' }}>
          Lặp #{currentCard?.repetitions || 0} · EF {currentCard?.ease_factor?.toFixed(1)}
        </span>
      </div>

      <div className="flashcard-progress">
        <div className="progress-bar"><div className="progress-fill" style={{ width: `${progress}%`, background: 'var(--gold)' }} /></div>
        <div className="progress-text">{index + 1} / {cards.length} thẻ cần ôn</div>
      </div>

      <div className="flashcard-container" onClick={() => setFlipped(f => !f)}>
        <div className={`flashcard-inner ${flipped ? 'flipped' : ''}`}>
          <div className="flashcard-face front">
            <div className="card-hint">Từ cần ôn</div>
            <div className="card-front-text">{currentCard.front}</div>
            {isJa && currentCard.reading && <div className="card-reading">{currentCard.reading}</div>}
            <div className="flip-hint">Nhớ nghĩa chưa? Nhấn để kiểm tra · Space</div>
          </div>
          <div className="flashcard-face back">
            <div className="card-hint">Nghĩa</div>
            <div className="card-back-text">{currentCard.back}</div>
            {currentCard.example && <div className="card-example">{currentCard.example}</div>}
            <div className="flip-hint">Đánh giá mức độ nhớ</div>
          </div>
        </div>
      </div>

      {flipped && (
        <div className="quality-section">
          <h4>Bạn nhớ tốt đến đâu?</h4>
          <div className="quality-buttons">
            <button className="quality-btn q-again" onClick={() => handleQuality(1, 'again')}>
              😵 Quên<span>{getNextInterval(1)} · 1</span>
            </button>
            <button className="quality-btn q-hard" onClick={() => handleQuality(3, 'hard')}>
              😓 Khó<span>{getNextInterval(3)} · 2</span>
            </button>
            <button className="quality-btn q-good" onClick={() => handleQuality(4, 'good')}>
              😊 Nhớ<span>{getNextInterval(4)} · 3</span>
            </button>
            <button className="quality-btn q-easy" onClick={() => handleQuality(5, 'easy')}>
              🚀 Dễ<span>{getNextInterval(5)} · 4</span>
            </button>
          </div>
        </div>
      )}

      <p style={{ textAlign: 'center', color: 'var(--ink-muted)', fontSize: '0.75rem', marginTop: '1rem' }}>
        Phím tắt: Space = lật thẻ · 1/2/3/4 = đánh giá
      </p>
    </div>
  );
}
